/**
 * トピックマップ用ヘルパー関数
 * Sunburstチャート用のデータ変換とスコア計算を提供
 */

/**
 * 分析データからトピックマップ用データを抽出
 * @param {Object} competitorData - 競合分析データ
 * @returns {Object} 抽出されたデータ
 */
export const extractTopicMapData = competitorData => {
  if (!competitorData) {
    return {
      mainQuery: 'メインクエリ',
      categories: [],
      topicScores: [],
      hasData: false,
    };
  }

  // 基本データ取得
  const mainQuery = competitorData.keyword || 'メインクエリ';

  // MECEカテゴリー構造（新構造） - 複数パスを試行
  let categories = [];

  // パス1: topic_analysis.topic_extraction.main_categories (新構造)
  const newStructureCategories =
    competitorData?.topic_analysis?.topic_extraction?.main_categories;
  if (
    Array.isArray(newStructureCategories) &&
    newStructureCategories.length > 0
  ) {
    categories = newStructureCategories;
  } else {
    // パス2: 直接topic_analysis.main_categories (フォールバック)
    const directCategories = competitorData?.topic_analysis?.main_categories;
    if (Array.isArray(directCategories) && directCategories.length > 0) {
      categories = directCategories;
    } else {
      // パス3: ルートレベルmain_categories (さらなるフォールバック)
      const rootCategories = competitorData?.main_categories;
      if (Array.isArray(rootCategories) && rootCategories.length > 0) {
        categories = rootCategories;
      }
    }
  }

  // ヒートマップスコア - 複数のパスを試行
  let topicScores = [];

  // パス1: integrated_heatmap.formatted_heatmaps.topic_heatmap.rows
  const integratedHeatmap =
    competitorData?.topic_analysis?.integrated_heatmap?.formatted_heatmaps
      ?.topic_heatmap?.rows;

  // パス2: topic_analysis.topic_heatmap.rows (フォールバック)
  const directHeatmap = competitorData?.topic_analysis?.topic_heatmap?.rows;

  // パス3: heatmaps.topic_heatmap.rows (さらなるフォールバック)
  const heatmapsPath = competitorData?.heatmaps?.topic_heatmap?.rows;

  if (Array.isArray(integratedHeatmap) && integratedHeatmap.length > 0) {
    topicScores = integratedHeatmap;
  } else if (Array.isArray(directHeatmap) && directHeatmap.length > 0) {
    topicScores = directHeatmap;
  } else if (Array.isArray(heatmapsPath) && heatmapsPath.length > 0) {
    topicScores = heatmapsPath;
  }

  // データの存在確認を修正
  const hasValidCategories = Array.isArray(categories) && categories.length > 0;
  const hasValidTopicScores =
    Array.isArray(topicScores) && topicScores.length > 0;
  const hasData = hasValidCategories && hasValidTopicScores;

  console.log('🔍 [extractTopicMapData] Extracted data:', {
    mainQuery,
    categoriesCount: categories.length,
    topicScoresCount: topicScores.length,
    hasData,
    hasValidCategories,
    hasValidTopicScores,
    categories: categories.map(cat => ({
      name: cat.category_name,
      topicsCount: cat.unified_topics?.length || 0,
    })),
    topicScoresSample: topicScores.slice(0, 3).map(score => ({
      topic: score.topic || score.name,
      average_score: score.average_score,
    })),
    dataStructure: {
      integratedHeatmapExists: !!integratedHeatmap,
      directHeatmapExists: !!directHeatmap,
      heatmapsPathExists: !!heatmapsPath,
    },
    // 詳細デバッグ情報
    rawCompetitorData: {
      hasTopicAnalysis: !!competitorData?.topic_analysis,
      topicAnalysisKeys: competitorData?.topic_analysis
        ? Object.keys(competitorData.topic_analysis)
        : [],
      hasTopicExtraction: !!competitorData?.topic_analysis?.topic_extraction,
      topicExtractionKeys: competitorData?.topic_analysis?.topic_extraction
        ? Object.keys(competitorData.topic_analysis.topic_extraction)
        : [],
      // カテゴリーパスの詳細確認
      categoryPaths: {
        newStructure: {
          exists: !!newStructureCategories,
          isArray: Array.isArray(newStructureCategories),
          length: newStructureCategories?.length || 0,
        },
        directStructure: {
          exists: !!competitorData?.topic_analysis?.main_categories,
          isArray: Array.isArray(
            competitorData?.topic_analysis?.main_categories,
          ),
          length: competitorData?.topic_analysis?.main_categories?.length || 0,
        },
        rootStructure: {
          exists: !!competitorData?.main_categories,
          isArray: Array.isArray(competitorData?.main_categories),
          length: competitorData?.main_categories?.length || 0,
        },
      },
      selectedCategoryPath:
        categories === newStructureCategories
          ? 'newStructure'
          : categories === competitorData?.topic_analysis?.main_categories
            ? 'directStructure'
            : categories === competitorData?.main_categories
              ? 'rootStructure'
              : 'none',
    },
  });

  return {
    mainQuery,
    categories,
    topicScores,
    hasData,
  };
};

/**
 * トピックスコアのマップを作成
 * @param {Array} topicScores - トピックスコア配列
 * @returns {Map} トピック名 -> スコアのマップ
 */
export const createScoreMap = topicScores => {
  const scoreMap = new Map();

  if (!Array.isArray(topicScores)) {
    console.warn(
      '🔍 [createScoreMap] topicScores is not an array:',
      typeof topicScores,
    );
    return scoreMap;
  }

  topicScores.forEach((row, index) => {
    // 複数のフィールド名を試行
    const topicName = row.topic || row.name || row.topic_name || '';

    // 複数のスコアフィールドを試行
    let score = 0;
    if (typeof row.average_score === 'number') {
      score = row.average_score;
    } else if (typeof row.average_normalized_score === 'number') {
      score = row.average_normalized_score;
    } else if (typeof row.score === 'number') {
      score = row.score;
    } else if (typeof row.normalized_score === 'number') {
      score = row.normalized_score;
    } else {
      // 文字列の場合はparseFloat
      score = parseFloat(
        row.average_score ||
          row.average_normalized_score ||
          row.score ||
          row.normalized_score ||
          0,
      );
    }

    if (topicName && !isNaN(score)) {
      scoreMap.set(topicName, score);
    }

    // デバッグ用（最初の3件のみ）
    if (index < 3) {
      console.log(`🔍 [createScoreMap] Row ${index}:`, {
        topicName,
        score,
        rawRow: row,
      });
    }
  });

  const scores = Array.from(scoreMap.values()).filter(s => !isNaN(s) && s > 0);

  console.log('🔍 [createScoreMap] Score map created:', {
    totalTopics: scoreMap.size,
    validScores: scores.length,
    scoreRange:
      scores.length > 0
        ? {
            min: Math.min(...scores),
            max: Math.max(...scores),
          }
        : { min: 0, max: 0 },
    sampleEntries: Array.from(scoreMap.entries()).slice(0, 5),
  });

  return scoreMap;
};

/**
 * カテゴリーの平均スコアを計算
 * @param {Array} topics - カテゴリー内のトピック配列
 * @param {Map} scoreMap - スコアマップ
 * @returns {number} 平均スコア
 */
export const calculateCategoryScore = (topics, scoreMap) => {
  if (!Array.isArray(topics) || topics.length === 0) {
    return 0;
  }

  const scores = topics
    .map(topic => scoreMap.get(topic) || 0)
    .filter(score => score > 0);

  if (scores.length === 0) {
    return 0;
  }

  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

/**
 * 全体の平均スコアを計算
 * @param {Map} scoreMap - スコアマップ
 * @returns {number} 全体平均スコア
 */
export const calculateOverallScore = scoreMap => {
  const allScores = Array.from(scoreMap.values()).filter(score => score > 0);

  if (allScores.length === 0) {
    return 0;
  }

  return allScores.reduce((sum, score) => sum + score, 0) / allScores.length;
};

/**
 * Sunburst用の階層データを構築
 * @param {string} mainQuery - メインクエリ
 * @param {Array} categories - カテゴリー配列
 * @param {Map} scoreMap - スコアマップ
 * @returns {Object} Sunburst用階層データ
 */
export const buildHierarchicalData = (mainQuery, categories, scoreMap) => {
  console.log('🔍 [buildHierarchicalData] Input:', {
    mainQuery,
    categoriesCount: categories.length,
    scoreMapSize: scoreMap.size,
    categoriesSample: categories.slice(0, 2).map(cat => ({
      category_name: cat.category_name,
      unified_topics: cat.unified_topics,
    })),
  });

  // カテゴリーデータを構築
  const categoryChildren = categories.map(category => {
    const categoryTopics = category.unified_topics || [];
    
    console.log('🔍 [buildHierarchicalData] Processing category:', {
      category_name: category.category_name,
      topicsCount: categoryTopics.length,
      topicsSample: categoryTopics.slice(0, 3),
    });

    // トピック名を抽出する関数
    const getTopicName = topic => {
      if (typeof topic === 'string') return topic;
      return topic.name || topic.topic || topic.topic_name || 'トピック名なし';
    };

    // カテゴリースコアを計算（トピック名のリストを作成）
    const topicNames = categoryTopics.map(getTopicName);
    const categoryScore = calculateCategoryScore(topicNames, scoreMap);

    // トピックデータを構築
    const topicChildren = categoryTopics.map(topic => {
      const topicName = getTopicName(topic);
      const score = scoreMap.get(topicName) || 0;
      
      return {
        name: topicName,
        value: score,
        itemStyle: {
          borderWidth: 1,
          borderColor: '#fff',
        },
      };
    });

    return {
      name: category.category_name || 'カテゴリー',
      value: categoryScore,
      children: topicChildren,
      itemStyle: {
        borderWidth: 2,
        borderColor: '#fff',
      },
    };
  });

  // 全体スコアを計算
  const overallScore = calculateOverallScore(scoreMap);

  const hierarchicalData = {
    name: mainQuery,
    value: overallScore,
    children: categoryChildren,
    itemStyle: {
      borderWidth: 3,
      borderColor: '#fff',
    },
  };

  console.log('🔍 [buildHierarchicalData] Hierarchical data built:', {
    mainQuery,
    overallScore: overallScore.toFixed(2),
    categoriesCount: categoryChildren.length,
    totalTopics: categoryChildren.reduce(
      (sum, cat) => sum + cat.children.length,
      0,
    ),
    sampleCategory: categoryChildren[0],
  });

  return hierarchicalData;
};

/**
 * ECharts Sunburst用のオプションを生成
 * @param {Object} hierarchicalData - 階層データ
 * @param {Object} config - 設定オプション
 * @returns {Object} EChartsオプション
 */
export const generateSunburstOption = (hierarchicalData, config = {}) => {
  const { height = 500, showVisualMap = true, enableAnimation = true } = config;

  return {
    // VisualMap設定（紫系統の濃淡）
    visualMap: showVisualMap
      ? {
          type: 'continuous',
          min: 0,
          max: 10,
          inRange: {
            color: [
              '#f3f0ff', // 最も薄い紫
              '#e9e5ff', // 薄い紫
              '#d6ccff', // やや薄い紫
              '#c4b5fd', // 中程度の薄い紫
              '#a78bfa', // 中程度の紫
              '#8b5cf6', // やや濃い紫
              '#7c3aed', // 濃い紫
              '#6d28d9', // より濃い紫
              '#5b21b6', // 非常に濃い紫
              '#4c1d95', // 最も濃い紫
              '#3730a3', // 極濃紫
            ],
          },
          calculable: true,
          orient: 'vertical',
          left: 'left',
          top: 'center',
          textStyle: {
            fontSize: 12,
            color: '#4c1d95',
          },
          formatter: value => `${value.toFixed(1)}`,
        }
      : undefined,

    // ツールチップ設定
    tooltip: {
      trigger: 'item',
      formatter: params => {
        const { name, value } = params.data;
        return `
          <div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${name}</div>
            <div style="color: #666;">スコア: <span style="color: #7c3aed; font-weight: bold;">${value.toFixed(1)}</span></div>
          </div>
        `;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#7c3aed',
      borderWidth: 1,
      textStyle: {
        color: '#333',
      },
    },

    // アニメーション設定
    animation: enableAnimation,
    animationDuration: 1000,
    animationEasing: 'cubicOut',

    // Sunburst設定（Drink スタイル）
    series: [
      {
        type: 'sunburst',
        data: [hierarchicalData],
        radius: [15, 80],
        center: ['50%', '50%'],
        sort: null,
        emphasis: {
          focus: 'ancestor',
        },
        levels: [
          {},
          {
            // 第1階層（カテゴリー）
            r0: 15,
            r: 45,
            itemStyle: {
              borderWidth: 2,
              borderColor: '#fff',
            },
            label: {
              align: 'right',
              fontSize: 12,
              fontWeight: 'bold',
              color: '#4c1d95',
            },
          },
          {
            // 第2階層（トピック）
            r0: 45,
            r: 70,
            itemStyle: {
              borderWidth: 1,
              borderColor: '#fff',
            },
            label: {
              position: 'outside',
              padding: 3,
              quiet: true,
              fontSize: 10,
              color: '#4c1d95',
              formatter: function (param) {
                return param.name.length > 20
                  ? param.name.substring(0, 20) + '...'
                  : param.name;
              },
            },
            labelLine: {
              length: 10,
              smooth: 0.2,
              lineStyle: {
                color: '#7c3aed',
                width: 1,
              },
            },
          },
          {
            // 第3階層（詳細トピック - 必要に応じて）
            r0: 70,
            r: 80,
            itemStyle: {
              borderWidth: 1,
              borderColor: '#fff',
            },
            label: {
              position: 'outside',
              padding: 3,
              silent: true,
              fontSize: 9,
              color: '#666',
            },
            labelLine: {
              length: 15,
              smooth: 0.2,
              lineStyle: {
                color: '#a78bfa',
                width: 1,
              },
            },
          },
        ],
      },
    ],
  };
};

/**
 * ECharts Tree用のオプションを生成
 * @param {Object} hierarchicalData - 階層データ
 * @param {Object} config - 設定オプション
 * @returns {Object} EChartsオプション
 */
export const generateTreeOption = (hierarchicalData, config = {}) => {
  const { height = 700, showVisualMap = true, enableAnimation = true } = config;

  return {
    // VisualMap設定（青系統の濃淡 - コンテンツ戦略と統一）
    visualMap: showVisualMap
      ? {
          type: 'continuous',
          min: 0,
          max: 10,
          inRange: {
            color: [
              '#f0f9ff', // 最も薄い青（コンテンツ戦略と同じ）
              '#e6f7ff', // 薄い青
              '#bae7ff', // やや薄い青
              '#91d5ff', // 中程度の薄い青
              '#69c0ff', // 中程度の青
              '#40a9ff', // やや濃い青
              '#1890ff', // 濃い青（コンテンツ戦略のメイン色）
              '#096dd9', // より濃い青
              '#0050b3', // 非常に濃い青
              '#003a8c', // 最も濃い青
              '#002766', // 極濃青
            ],
          },
          calculable: true,
          orient: 'vertical',
          left: 'left',
          top: 'top', // 左上に配置
          textStyle: {
            fontSize: 12,
            color: '#1890ff',
            fontFamily:
              '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
          },
          formatter: value => `${value.toFixed(1)}`,
        }
      : undefined,

    // ツールチップ設定
    tooltip: {
      trigger: 'item',
      formatter: params => {
        const { name, value } = params.data;

        const scoreText = value ? value.toFixed(1) : 'N/A';

        return `
          <div style="padding: 12px; max-width: 320px; font-family: 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', Meiryo, sans-serif;">
            <div style="font-weight: 500; margin-bottom: 8px; font-size: 14px; color: #1890ff; line-height: 1.4;">${name}</div>
            <div style="color: #666; font-size: 12px;">
              スコア: <span style="color: #1890ff; font-weight: 500; font-size: 13px;">${scoreText}</span>
            </div>
          </div>
        `;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#1890ff',
      borderWidth: 1,
      borderRadius: 8,
      textStyle: {
        color: '#333',
        fontFamily:
          '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
      },
      extraCssText:
        'box-shadow: 0 2px 8px rgba(24, 144, 255, 0.12); backdrop-filter: blur(4px);',
    },

    // アニメーション設定
    animation: enableAnimation,
    animationDuration: 1200,
    animationEasing: 'elasticOut',

    // Tree設定
    series: [
      {
        type: 'tree',
        data: [hierarchicalData],
        top: '5%', // マージンを最小化
        left: '10%', // フィルター分の最小マージン
        bottom: '3%', // 下マージンを最小化
        right: '40%', // 右マージンを大幅に拡大（表示幅を狭くする）
        symbolSize: (value, params) => {
          // スコアに基づいてノードサイズを調整（小さく調整）
          const score = params.data.value || 0;
          if (params.data.children && params.data.children.length > 0) {
            // カテゴリーノード（小さく）
            return score > 5 ? 12 : 8;
          } else {
            // トピックノード（小さく）
            return Math.max(6, Math.min(14, score * 1.5));
          }
        },
        label: {
          fontSize: 14,
          fontWeight: 500,
          fontFamily:
            '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
          color: '#1890ff',
          formatter: function (params) {
            const name = params.data.name;

            // カテゴリー（子要素あり）の場合
            if (params.data.children && params.data.children.length > 0) {
              return `{category|${name}}`;
            } else {
              // トピック（葉ノード）の場合
              return `{topic|${name}}`;
            }
          },
          position: 'bottom', // カテゴリーは下に配置
          verticalAlign: 'top',
          align: 'center',
          rich: {
            category: {
              fontSize: 14,
              fontWeight: 500,
              fontFamily:
                '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
              color: '#1890ff',
              lineHeight: 20, // 行間を広げる
              padding: [2, 0], // 上下のパディング追加
            },
            topic: {
              fontSize: 14,
              fontWeight: 500,
              fontFamily:
                '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
              color: '#40a9ff',
              lineHeight: 20, // 行間を広げる
              padding: [2, 0], // 上下のパディング追加
            },
          },
        },
        leaves: {
          label: {
            position: 'right', // トピックは右に配置
            verticalAlign: 'middle',
            align: 'left',
            fontFamily:
              '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
          },
        },
        emphasis: {
          focus: 'descendant',
        },
        expandAndCollapse: true,
        animationDuration: 550,
        animationDurationUpdate: 750,
        roam: 'move', // ドラッグ移動のみ有効（ズーム無効）
        layout: 'orthogonal', // レイアウトを直交型に設定
        orient: 'LR', // 左から右への配置
        // 間隔調整の設定（距離をさらに短縮）
        initialTreeDepth: -1, // 全展開
        edgeShape: 'curve', // 曲線接続
        edgeForkPosition: '2%', // 分岐点をさらに早く（距離を極限まで短縮）
        // レイアウト設定（表示幅を狭く制限）
        layoutCenter: ['30%', '50%'], // 左寄り配置
        layoutSize: ['50%', '90%'], // 幅を50%に制限（高さは維持）
        // ノード間隔の直接制御
        nodeGap: 5, // ノード間の最小間隔
        layerGap: 30, // 階層間の間隔を最小化
        itemStyle: {
          color: params => {
            const value = params.data.value || 0;
            // スコアに基づいて色を決定（青系統グラデーション）
            if (value >= 8) return '#003a8c'; // 超高スコア
            if (value >= 6) return '#0050b3'; // 高スコア
            if (value >= 4) return '#1890ff'; // 中スコア
            if (value >= 2) return '#40a9ff'; // 低スコア
            return '#91d5ff'; // 最低スコア
          },
          borderColor: '#fff',
          borderWidth: 3,
          shadowBlur: 3,
          shadowColor: 'rgba(24, 144, 255, 0.1)',
        },
        lineStyle: {
          color: '#91d5ff',
          width: 1,
          curveness: 0.2, // 曲線を復元
          opacity: 0.8,
        },
      },
    ],
  };
};

/**
 * フォールバック用の空データを生成
 * @param {string} mainQuery - メインクエリ
 * @returns {Object} 空の階層データ
 */
export const generateEmptyData = (mainQuery = 'メインクエリ') => {
  return {
    name: mainQuery,
    value: 0,
    children: [
      {
        name: 'データを読み込み中...',
        value: 0,
        children: [
          {
            name: 'しばらくお待ちください',
            value: 0,
          },
        ],
      },
    ],
  };
};
